import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  Heart,
  LogIn,
  LogOut,
  MapPin,
  MessageSquare,
  Settings,
  SlidersHorizontal,
  User,
  UserPlus,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useNotifications } from '@/context/NotificationContext';
import { useCatalogPath } from '@/hooks/useRouteLocale';
import { disconnectSocket } from '@/services/socket';
import { safeRemoteUrl, ImagePresets } from '@/utils/safeImageUrl';

interface MenuItemProps {
  icon: LucideIcon;
  label: string;
  to?: string;
  onClick?: () => void;
  danger?: boolean;
  badge?: number;
}

function ItemBadge({ count }: { count: number }) {
  if (count <= 0) return null;
  return (
    <span className="ml-auto flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-bold text-white">
      {count > 99 ? '99+' : count}
    </span>
  );
}

export function UserMenu() {
  const { user, signOut } = useAuth();
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();
  // Catalog links: `/pk/en/…` once the URL move is live (web plan W2.1).
  const catalog = useCatalogPath();
  const [open, setOpen] = useState(false);
  const [brokenAvatarSrc, setBrokenAvatarSrc] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onMouseDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [open]);

  const close = () => setOpen(false);

  const handleLogout = () => {
    if (!window.confirm('Log out of PromoStork?')) return;
    close();
    disconnectSocket();
    void signOut();
    navigate(catalog('/'));
  };

  const MenuItem = ({ icon: Icon, label, to, onClick, danger, badge }: MenuItemProps) => {
    const className = `flex w-full items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors duration-200 hover:bg-line-light ${
      danger ? 'text-error' : 'text-ink'
    }`;
    const content = (
      <>
        <Icon size={18} className={danger ? 'text-error' : 'text-ink-secondary'} />
        <span className="truncate">{label}</span>
        {badge !== undefined && <ItemBadge count={badge} />}
      </>
    );
    if (to) {
      return (
        <Link to={to} className={className} onClick={close}>
          {content}
        </Link>
      );
    }
    return (
      <button type="button" className={className} onClick={onClick}>
        {content}
      </button>
    );
  };

  const avatarSrc = user?.avatar ? safeRemoteUrl(user.avatar, ImagePresets.thumb) ?? user.avatar : null;
  const showAvatar = avatarSrc !== null && avatarSrc !== brokenAvatarSrc;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-full border border-line transition-all duration-200 hover:border-primary/40"
        aria-label="Account menu"
        aria-expanded={open}
      >
        {user ? (
          showAvatar ? (
            <img
              src={avatarSrc}
              alt={user.fullName}
              className="h-full w-full object-cover"
              onError={() => setBrokenAvatarSrc(avatarSrc)}
            />
          ) : (
            <span className="flex h-full w-full items-center justify-center bg-primary/15 font-bold text-primary">
              {user.fullName.charAt(0).toUpperCase()}
            </span>
          )
        ) : (
          <User size={18} className="text-ink-secondary" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full z-50 mt-2 w-64 animate-fade-up rounded-2xl border border-line bg-surface py-2 shadow-xl">
          {user ? (
            <>
              <div className="border-b border-line-light px-4 py-3">
                <p className="truncate font-bold text-ink">{user.fullName}</p>
                <p className="truncate text-xs text-ink-secondary">{user.email}</p>
              </div>
              <MenuItem icon={User} label="Profile" to="/profile" />
              <MenuItem icon={Heart} label="Favorites" to="/favorites" />
              <MenuItem icon={Bell} label="Notifications" to="/notifications" badge={unreadCount} />
              <MenuItem
                icon={SlidersHorizontal}
                label="Notification settings"
                to="/notification-settings"
              />
              <MenuItem icon={Settings} label="Settings" to="/settings" />
              <MenuItem icon={MessageSquare} label="Send feedback" to="/feedback" />
              <div className="my-1 border-t border-line-light" />
              <MenuItem icon={LogOut} label="Log out" onClick={handleLogout} danger />
            </>
          ) : (
            <>
              <div className="border-b border-line-light px-4 py-3">
                <p className="truncate font-bold text-ink">Welcome, guest</p>
                <p className="text-xs text-ink-secondary">
                  Log in to follow brands &amp; get notified
                </p>
              </div>
              <MenuItem icon={LogIn} label="Log in" to="/login" />
              <MenuItem icon={UserPlus} label="Create account" to="/register" />
              <div className="my-1 border-t border-line-light" />
              <MenuItem icon={MapPin} label="Nearby" to={catalog('/nearby')} />
              <MenuItem icon={Heart} label="Favorites" to="/login" />
              <MenuItem icon={MessageSquare} label="Send feedback" to="/feedback" />
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default UserMenu;
